import {
  Field,
  FieldGroup,
  Fieldset,
  Label,
  Legend,
  Description,
  ErrorMessage
} from '@oysterjs/ui/fieldset';
import { Input } from '@oysterjs/ui/input';
import { Select } from '@oysterjs/ui/select';
import { Checkbox, CheckboxField } from '@oysterjs/ui/checkbox';
import { CaraWordMark } from '@oysterjs/ui/logo';
import { Divider } from '@oysterjs/ui/divider';
import { Text } from '@oysterjs/ui/text';
import { Button } from '@oysterjs/ui/button';
import { Form } from 'react-router-dom';
import { states } from '@oysterjs/core/states';
import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { useFormActions } from '@oysterjs/core/graphql/form';
import { ValidationError } from '@oysterjs/core/graphql';
import { CreateAgencyInput, Role, useCreateAgencyMutation } from '~/api/graphql';
import { getApplicationError, getValidationErrors } from '@oysterjs/core/graphql/errors';
const handleToMeetingUrlMap = {
  vic: 'https://meetings.hubspot.com/vic-yeh/cara-onboarding?embed=true',
  jon: 'https://meetings.hubspot.com/jonathan-patel/onboarding?embed=true',
  nikhil: 'https://meetings.hubspot.com/nikhil-kansal?embed=true',
  chris: 'https://meetings.hubspot.com/christopher-gosselin/cara-onboarding?embed=true',
  austin: 'https://meetings.hubspot.com/austin-strazzulla/cara-onboarding-meeting?embed=true',
  austinm: 'https://meetings.hubspot.com/austin-malone/cara-onboarding?embed=true',
  sam: 'https://meetings.hubspot.com/sam-bartner/cara-demo?embed=true',
  jason: 'https://meetings.hubspot.com/jason-hoeppner/cara-onboarding?embed=true'
};

// Helper function to build meeting URL with query parameters
const getMeetingUrl = (
  handle: string,
  userName?: string,
  userEmail?: string,
  companyName?: string
) => {
  const baseUrl = handleToMeetingUrlMap[handle];
  if (!baseUrl) return '';

  const params = new URLSearchParams();

  // Add first user's name and email
  if (userName) {
    const nameParts = userName.trim().split(/\s+/);
    const firstName = nameParts[0] || '';
    const lastName = nameParts.slice(1).join(' ') || '';

    if (firstName) params.append('firstName', firstName);
    if (lastName) params.append('lastName', lastName);
  }

  if (userEmail) {
    params.append('email', userEmail);
  }

  // Add company name
  if (companyName) {
    params.append('companyName', companyName);
    params.append('company', companyName);
  }

  const queryString = params.toString();
  return queryString ? `${baseUrl}&${queryString}` : baseUrl;
};

export const SignUpPage = () => {
  let { handle } = useParams();

  // Fallback to vic if handle is undefined (which should be impossible because of how the routes are currently set up)
  if (!handle) {
    handle = 'vic';
  }

  const [createAgency, { loading, error: createAgencyError }] = useCreateAgencyMutation();
  const [useAgencyAddressAsProducer, setUseAgencyAddressAsProducer] = useState(true);
  const [meetingBooked, setMeetingBooked] = useState<Date | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [validationErrors, setValidationErrors] = useState<ValidationError[]>([]);
  const [form, setForm] = useState<CreateAgencyInput>({
    agency: {
      name: '',
      displayName: '',
      phone: '',
      logoUrl: '',
      wordmarkUrl: '',
      mailingAddress: {
        line1: '',
        line2: '',
        city: '',
        zone: '',
        postalCode: ''
      },
      configuration: {
        liabilityCOIConfiguration: {
          producer: {
            name: '',
            address: {
              line1: '',
              line2: '',
              city: '',
              zone: '',
              postalCode: ''
            }
          },
          contactName: '',
          contactEmail: '',
          contactPhone: '',
          authorizedRepName: '',
          renderAllCaps: false
        }
      }
    },
    users: [
      { name: '', email: '', roles: [Role.Admin] },
      { name: '', email: '', roles: [Role.Admin] },
      { name: '', email: '', roles: [Role.Admin] },
      { name: '', email: '', roles: [Role.Admin] },
      { name: '', email: '', roles: [Role.Admin] }
    ]
  });
  const { updateForm, getValidationError } = useFormActions(setForm, setValidationErrors);

  // Extract mutation errors
  useEffect(() => {
    if (createAgencyError) {
      const errors = getValidationErrors(createAgencyError);
      if (errors?.length) {
        setValidationErrors(errors);
        setError(
          'There were validation errors in your submission. Please check the form and try again.'
        );
        return;
      }

      const applicationError = getApplicationError(createAgencyError);
      if (applicationError) {
        setError(applicationError.message);
        return;
      }

      setError(createAgencyError.message);
    }
  }, [createAgencyError]);

  // Handle form submission
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    // Update the form to remove empty users
    setForm((prev) => ({
      ...prev,
      users: prev.users.filter((user) => user.name.trim() !== '' || user.email.trim() !== '')
    }));

    try {
      const result = await createAgency({
        variables: {
          input: {
            ...form,
            // Propagate the onboarding date from the state variable
            onboardingDate: meetingBooked || undefined,
            // The form update from above won't reflect here, so make the update again
            users: form.users.filter((user) => user.name.trim() !== '' || user.email.trim() !== ''),
            // Copy agency address to producer address if checkbox is checked
            agency: {
              ...form.agency,
              configuration: {
                ...form.agency.configuration,
                liabilityCOIConfiguration: {
                  ...form.agency.configuration.liabilityCOIConfiguration,
                  producer: {
                    ...form.agency.configuration.liabilityCOIConfiguration.producer,
                    address: useAgencyAddressAsProducer
                      ? form.agency.mailingAddress
                      : form.agency.configuration.liabilityCOIConfiguration.producer.address
                  }
                }
              }
            }
          }
        }
      });

      if (result.data?.createAgency?.id) {
        setSuccess(true);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An unknown error occurred');
    }
  };

  useEffect(() => {
    // Listen for messages from the HubSpot meeting embed iframe
    const handleMessage = (event: MessageEvent) => {
      // Verify the message is coming from HubSpot's domain for security
      if (event.origin === 'https://meetings.hubspot.com') {
        if (event.data?.meetingBookSucceeded) {
          const meeting = event.data?.meetingsPayload?.bookingResponse?.event;
          const meetingTimeMs = meeting?.dateTime;

          if (meetingTimeMs) {
            setMeetingBooked(new Date(meetingTimeMs));
          }
        }
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, []);

  if (success) {
    return (
      <div className="w-full min-h-screen bg-neutral-50 dark:bg-neutral-900">
        <div className="mx-auto max-w-7xl px-8 pb-24 sm:px-12">
          <div className="mx-auto max-w-2xl">
            <div className="pt-24 pb-16 flex flex-col gap-8">
              <CaraWordMark scale={0.7} />
            </div>
            <h1 className="text-3xl font-bold mb-2">Account created successfully!</h1>
            <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8">
              Your account has been created successfully. We'll introduce you to Cara and show you
              how to sign in and use it during your onboarding call.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full min-h-screen bg-neutral-50 dark:bg-neutral-900">
      <div className="mx-auto max-w-7xl px-8 pb-24 sm:px-12">
        <div className="mx-auto max-w-2xl">
          <div className="pt-24 pb-16 flex flex-col gap-8">
            <CaraWordMark scale={0.7} />
          </div>
          <h1 className="text-3xl font-bold mb-2">Create your account</h1>
          <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8">
            To get started with Cara, please fill out the information below. We'll set up your
            account and schedule an onboarding call with you.
          </p>
          <Form onSubmit={handleSubmit} className="space-y-8">
            {/* Agency Information */}
            <Fieldset>
              <Legend>Agency Information</Legend>
              <Text>
                Basic information about your agency that will be used throughout the platform.
              </Text>
              <FieldGroup>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <Label>Legal Name</Label>
                    <Description>Official registered name of your agency</Description>
                    <Input
                      value={form.agency.name}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['agency', 'name'], (prev) => ({
                          ...prev,
                          agency: { ...prev.agency, name: value }
                        }));
                      }}
                      placeholder="ACME Insurance Agency, LLC"
                      invalid={!!getValidationError(validationErrors, ['agency', 'name'])}
                    />
                    {getValidationError(validationErrors, ['agency', 'name']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['agency', 'name'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Display Name</Label>
                    <Description>Name shown to clients and in the interface</Description>
                    <Input
                      value={form.agency.displayName}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['agency', 'displayName'], (prev) => ({
                          ...prev,
                          agency: { ...prev.agency, displayName: value }
                        }));
                      }}
                      placeholder="ACME Insurance"
                      invalid={!!getValidationError(validationErrors, ['agency', 'displayName'])}
                    />
                    {getValidationError(validationErrors, ['agency', 'displayName']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['agency', 'displayName'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <Label>Phone</Label>
                    <Description>Main contact number for your agency</Description>
                    <Input
                      value={form.agency.phone}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['agency', 'phone'], (prev) => ({
                          ...prev,
                          agency: { ...prev.agency, phone: value }
                        }));
                      }}
                      type="tel"
                      placeholder="(555) 555-5555"
                      invalid={!!getValidationError(validationErrors, ['agency', 'phone'])}
                    />
                    {getValidationError(validationErrors, ['agency', 'phone']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['agency', 'phone'])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>

            <Divider />

            {/* Agency Mailing Address */}
            <Fieldset>
              <Legend>Agency Mailing Address</Legend>
              <Text>
                The mailing address for your agency. This will be used for our records and for
                billing.
              </Text>
              <FieldGroup>
                <Field>
                  <Label>Address Line 1</Label>
                  <Input
                    value={form.agency.mailingAddress.line1}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'mailingAddress', 'line1'], (prev) => ({
                        ...prev,
                        agency: {
                          ...prev.agency,
                          mailingAddress: {
                            ...prev.agency.mailingAddress,
                            line1: value
                          }
                        }
                      }));
                    }}
                    placeholder="123 Main Street"
                    invalid={
                      !!getValidationError(validationErrors, ['agency', 'mailingAddress', 'line1'])
                    }
                  />
                  {getValidationError(validationErrors, ['agency', 'mailingAddress', 'line1']) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, ['agency', 'mailingAddress', 'line1'])}
                    </ErrorMessage>
                  )}
                </Field>
                <Field>
                  <Label>Address Line 2</Label>
                  <Input
                    value={form.agency.mailingAddress.line2 || ''}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(['agency', 'mailingAddress', 'line2'], (prev) => ({
                        ...prev,
                        agency: {
                          ...prev.agency,
                          mailingAddress: {
                            ...prev.agency.mailingAddress,
                            line2: value
                          }
                        }
                      }));
                    }}
                    placeholder="Suite 200"
                  />
                </Field>
                <div className="grid grid-cols-3 gap-4">
                  <Field>
                    <Label>City</Label>
                    <Input
                      value={form.agency.mailingAddress.city}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['agency', 'mailingAddress', 'city'], (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            mailingAddress: {
                              ...prev.agency.mailingAddress,
                              city: value
                            }
                          }
                        }));
                      }}
                      placeholder="San Francisco"
                      invalid={
                        !!getValidationError(validationErrors, ['agency', 'mailingAddress', 'city'])
                      }
                    />
                    {getValidationError(validationErrors, ['agency', 'mailingAddress', 'city']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['agency', 'mailingAddress', 'city'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>State</Label>
                    <Select
                      value={form.agency.mailingAddress.zone}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['agency', 'mailingAddress', 'zone'], (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            mailingAddress: {
                              ...prev.agency.mailingAddress,
                              zone: value
                            }
                          }
                        }));
                      }}
                      invalid={
                        !!getValidationError(validationErrors, ['agency', 'mailingAddress', 'zone'])
                      }
                    >
                      <option value="">Select state</option>
                      {states.map((state) => (
                        <option key={state.value} value={state.value}>
                          {state.name}
                        </option>
                      ))}
                    </Select>
                    {getValidationError(validationErrors, ['agency', 'mailingAddress', 'zone']) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, ['agency', 'mailingAddress', 'zone'])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Postal Code</Label>
                    <Input
                      value={form.agency.mailingAddress.postalCode}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(['agency', 'mailingAddress', 'postalCode'], (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            mailingAddress: {
                              ...prev.agency.mailingAddress,
                              postalCode: value
                            }
                          }
                        }));
                      }}
                      placeholder="94102"
                      invalid={
                        !!getValidationError(validationErrors, [
                          'agency',
                          'mailingAddress',
                          'postalCode'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'agency',
                      'mailingAddress',
                      'postalCode'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'agency',
                          'mailingAddress',
                          'postalCode'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>

            <Divider />

            {/* Producer Information */}
            <Fieldset>
              <Legend>Producer Information</Legend>
              <Text>Producer details that will appear on ACORD forms and certificates.</Text>
              <FieldGroup>
                <Field>
                  <Label>Producer Name</Label>
                  <Input
                    value={form.agency.configuration.liabilityCOIConfiguration.producer.name}
                    onChange={(e) => {
                      const value = e.currentTarget.value;
                      updateForm(
                        [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'producer',
                          'name'
                        ],
                        (prev) => ({
                          ...prev,
                          agency: {
                            ...prev.agency,
                            configuration: {
                              ...prev.agency.configuration,
                              liabilityCOIConfiguration: {
                                ...prev.agency.configuration.liabilityCOIConfiguration,
                                producer: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration.producer,
                                  name: value
                                }
                              }
                            }
                          }
                        })
                      );
                    }}
                    placeholder="ACME Insurance Agency"
                    invalid={
                      !!getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'producer',
                        'name'
                      ])
                    }
                  />
                  {getValidationError(validationErrors, [
                    'agency',
                    'configuration',
                    'liabilityCOIConfiguration',
                    'producer',
                    'name'
                  ]) && (
                    <ErrorMessage>
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'producer',
                        'name'
                      ])}
                    </ErrorMessage>
                  )}
                </Field>

                <CheckboxField>
                  <Checkbox
                    checked={useAgencyAddressAsProducer}
                    onChange={setUseAgencyAddressAsProducer}
                    color="sky"
                  />
                  <Label>Use agency mailing address as producer address</Label>
                </CheckboxField>

                {!useAgencyAddressAsProducer && (
                  <>
                    <Field>
                      <Label>Producer Address Line 1</Label>
                      <Input
                        value={
                          form.agency.configuration.liabilityCOIConfiguration.producer.address.line1
                        }
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(
                            [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'line1'
                            ],
                            (prev) => ({
                              ...prev,
                              agency: {
                                ...prev.agency,
                                configuration: {
                                  ...prev.agency.configuration,
                                  liabilityCOIConfiguration: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration,
                                    producer: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer,
                                      address: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer.address,
                                        line1: value
                                      }
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="123 Main Street"
                        invalid={
                          !!getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'line1'
                          ])
                        }
                      />
                      {getValidationError(validationErrors, [
                        'agency',
                        'configuration',
                        'liabilityCOIConfiguration',
                        'producer',
                        'address',
                        'line1'
                      ]) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'producer',
                            'address',
                            'line1'
                          ])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Producer Address Line 2</Label>
                      <Input
                        value={
                          form.agency.configuration.liabilityCOIConfiguration.producer.address
                            .line2 || ''
                        }
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(
                            [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'line2'
                            ],
                            (prev) => ({
                              ...prev,
                              agency: {
                                ...prev.agency,
                                configuration: {
                                  ...prev.agency.configuration,
                                  liabilityCOIConfiguration: {
                                    ...prev.agency.configuration.liabilityCOIConfiguration,
                                    producer: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration
                                        .producer,
                                      address: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer.address,
                                        line2: value
                                      }
                                    }
                                  }
                                }
                              }
                            })
                          );
                        }}
                        placeholder="Suite 200"
                      />
                    </Field>
                    <div className="grid grid-cols-3 gap-4">
                      <Field>
                        <Label>City</Label>
                        <Input
                          value={
                            form.agency.configuration.liabilityCOIConfiguration.producer.address
                              .city
                          }
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            updateForm(
                              [
                                'agency',
                                'configuration',
                                'liabilityCOIConfiguration',
                                'producer',
                                'address',
                                'city'
                              ],
                              (prev) => ({
                                ...prev,
                                agency: {
                                  ...prev.agency,
                                  configuration: {
                                    ...prev.agency.configuration,
                                    liabilityCOIConfiguration: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration,
                                      producer: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer,
                                        address: {
                                          ...prev.agency.configuration.liabilityCOIConfiguration
                                            .producer.address,
                                          city: value
                                        }
                                      }
                                    }
                                  }
                                }
                              })
                            );
                          }}
                          placeholder="San Francisco"
                          invalid={
                            !!getValidationError(validationErrors, [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'city'
                            ])
                          }
                        />
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'producer',
                          'address',
                          'city'
                        ]) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'city'
                            ])}
                          </ErrorMessage>
                        )}
                      </Field>
                      <Field>
                        <Label>State</Label>
                        <Select
                          value={
                            form.agency.configuration.liabilityCOIConfiguration.producer.address
                              .zone
                          }
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            updateForm(
                              [
                                'agency',
                                'configuration',
                                'liabilityCOIConfiguration',
                                'producer',
                                'address',
                                'zone'
                              ],
                              (prev) => ({
                                ...prev,
                                agency: {
                                  ...prev.agency,
                                  configuration: {
                                    ...prev.agency.configuration,
                                    liabilityCOIConfiguration: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration,
                                      producer: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer,
                                        address: {
                                          ...prev.agency.configuration.liabilityCOIConfiguration
                                            .producer.address,
                                          zone: value
                                        }
                                      }
                                    }
                                  }
                                }
                              })
                            );
                          }}
                          invalid={
                            !!getValidationError(validationErrors, [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'zone'
                            ])
                          }
                        >
                          <option value="">Select state</option>
                          {states.map((state) => (
                            <option key={state.value} value={state.value}>
                              {state.name}
                            </option>
                          ))}
                        </Select>
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'producer',
                          'address',
                          'zone'
                        ]) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'zone'
                            ])}
                          </ErrorMessage>
                        )}
                      </Field>
                      <Field>
                        <Label>Postal Code</Label>
                        <Input
                          value={
                            form.agency.configuration.liabilityCOIConfiguration.producer.address
                              .postalCode
                          }
                          onChange={(e) => {
                            const value = e.currentTarget.value;
                            updateForm(
                              [
                                'agency',
                                'configuration',
                                'liabilityCOIConfiguration',
                                'producer',
                                'address',
                                'postalCode'
                              ],
                              (prev) => ({
                                ...prev,
                                agency: {
                                  ...prev.agency,
                                  configuration: {
                                    ...prev.agency.configuration,
                                    liabilityCOIConfiguration: {
                                      ...prev.agency.configuration.liabilityCOIConfiguration,
                                      producer: {
                                        ...prev.agency.configuration.liabilityCOIConfiguration
                                          .producer,
                                        address: {
                                          ...prev.agency.configuration.liabilityCOIConfiguration
                                            .producer.address,
                                          postalCode: value
                                        }
                                      }
                                    }
                                  }
                                }
                              })
                            );
                          }}
                          placeholder="94102"
                          invalid={
                            !!getValidationError(validationErrors, [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'postalCode'
                            ])
                          }
                        />
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'producer',
                          'address',
                          'postalCode'
                        ]) && (
                          <ErrorMessage>
                            {getValidationError(validationErrors, [
                              'agency',
                              'configuration',
                              'liabilityCOIConfiguration',
                              'producer',
                              'address',
                              'postalCode'
                            ])}
                          </ErrorMessage>
                        )}
                      </Field>
                    </div>
                  </>
                )}
              </FieldGroup>
            </Fieldset>

            <Divider />

            {/* Contact Information on ACORD forms */}
            <Fieldset>
              <Legend>Contact Information on ACORD forms</Legend>
              <Text>Contact details that will appear on ACORD forms and certificates.</Text>
              <FieldGroup>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <Label>Contact Name</Label>
                    <Input
                      value={form.agency.configuration.liabilityCOIConfiguration.contactName}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(
                          ['agency', 'configuration', 'liabilityCOIConfiguration', 'contactName'],
                          (prev) => ({
                            ...prev,
                            agency: {
                              ...prev.agency,
                              configuration: {
                                ...prev.agency.configuration,
                                liabilityCOIConfiguration: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration,
                                  contactName: value
                                }
                              }
                            }
                          })
                        );
                      }}
                      placeholder="John Smith"
                      invalid={
                        !!getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'contactName'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'contactName'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'contactName'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Authorized Representative</Label>
                    <Input
                      value={form.agency.configuration.liabilityCOIConfiguration.authorizedRepName}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(
                          [
                            'agency',
                            'configuration',
                            'liabilityCOIConfiguration',
                            'authorizedRepName'
                          ],
                          (prev) => ({
                            ...prev,
                            agency: {
                              ...prev.agency,
                              configuration: {
                                ...prev.agency.configuration,
                                liabilityCOIConfiguration: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration,
                                  authorizedRepName: value
                                }
                              }
                            }
                          })
                        );
                      }}
                      placeholder="Jane Doe"
                      invalid={
                        !!getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'authorizedRepName'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'authorizedRepName'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'authorizedRepName'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <Field>
                    <Label>Contact Email</Label>
                    <Input
                      value={form.agency.configuration.liabilityCOIConfiguration.contactEmail}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(
                          ['agency', 'configuration', 'liabilityCOIConfiguration', 'contactEmail'],
                          (prev) => ({
                            ...prev,
                            agency: {
                              ...prev.agency,
                              configuration: {
                                ...prev.agency.configuration,
                                liabilityCOIConfiguration: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration,
                                  contactEmail: value
                                }
                              }
                            }
                          })
                        );
                      }}
                      type="email"
                      placeholder="contact@acmeinsurance.com"
                      invalid={
                        !!getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'contactEmail'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'contactEmail'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'contactEmail'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                  <Field>
                    <Label>Contact Phone</Label>
                    <Input
                      value={form.agency.configuration.liabilityCOIConfiguration.contactPhone}
                      onChange={(e) => {
                        const value = e.currentTarget.value;
                        updateForm(
                          ['agency', 'configuration', 'liabilityCOIConfiguration', 'contactPhone'],
                          (prev) => ({
                            ...prev,
                            agency: {
                              ...prev.agency,
                              configuration: {
                                ...prev.agency.configuration,
                                liabilityCOIConfiguration: {
                                  ...prev.agency.configuration.liabilityCOIConfiguration,
                                  contactPhone: value
                                }
                              }
                            }
                          })
                        );
                      }}
                      type="tel"
                      placeholder="(555) 555-5555"
                      invalid={
                        !!getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'contactPhone'
                        ])
                      }
                    />
                    {getValidationError(validationErrors, [
                      'agency',
                      'configuration',
                      'liabilityCOIConfiguration',
                      'contactPhone'
                    ]) && (
                      <ErrorMessage>
                        {getValidationError(validationErrors, [
                          'agency',
                          'configuration',
                          'liabilityCOIConfiguration',
                          'contactPhone'
                        ])}
                      </ErrorMessage>
                    )}
                  </Field>
                </div>
              </FieldGroup>
            </Fieldset>

            <Divider />

            {/* Agency Users */}
            <Fieldset>
              <Legend>Agency Users</Legend>
              <Text>
                Add the users who will have access to your agency account. You can add more users
                after setup is complete.
              </Text>
              <FieldGroup>
                {Array.from({ length: form.users.length }).map((_, index) => (
                  <div key={index} className="grid grid-cols-[1fr_1fr_120px] gap-4">
                    <Field>
                      <Label>Name</Label>
                      <Input
                        value={form.users[index]?.name || ''}
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(['users', String(index), 'name'], (prev) => {
                            const newUsers = [...prev.users];
                            newUsers[index] = { ...newUsers[index], name: value };
                            return { ...prev, users: newUsers };
                          });
                        }}
                        placeholder="John Smith"
                        invalid={
                          !!getValidationError(validationErrors, ['users', String(index), 'name'])
                        }
                      />
                      {getValidationError(validationErrors, ['users', String(index), 'name']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['users', String(index), 'name'])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <Field>
                      <Label>Email</Label>
                      <Input
                        value={form.users[index]?.email || ''}
                        onChange={(e) => {
                          const value = e.currentTarget.value;
                          updateForm(['users', String(index), 'email'], (prev) => {
                            const newUsers = [...prev.users];
                            newUsers[index] = { ...newUsers[index], email: value };
                            return { ...prev, users: newUsers };
                          });
                        }}
                        type="email"
                        placeholder="john@acmeinsurance.com"
                        invalid={
                          !!getValidationError(validationErrors, ['users', String(index), 'email'])
                        }
                      />
                      {getValidationError(validationErrors, ['users', String(index), 'email']) && (
                        <ErrorMessage>
                          {getValidationError(validationErrors, ['users', String(index), 'email'])}
                        </ErrorMessage>
                      )}
                    </Field>
                    <div className="mt-[calc(2rem_-_1px)]">
                      {index > 0 && (
                        <Button
                          type="button"
                          color="white"
                          onClick={() => {
                            setForm((prev) => ({
                              ...prev,
                              users: prev.users.filter((_, i) => i !== index)
                            }));
                          }}
                          className="w-full h-10"
                        >
                          Remove
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
                <div className="mt-6">
                  <Button
                    type="button"
                    color="white"
                    onClick={() => {
                      setForm((prev) => ({
                        ...prev,
                        users: [...prev.users, { name: '', email: '', roles: [Role.Admin] }]
                      }));
                    }}
                  >
                    Add Another User
                  </Button>
                </div>
              </FieldGroup>
            </Fieldset>

            {/* Schedule Onboarding */}
            <Fieldset>
              <h1 className="text-2xl font-bold mb-2">Schedule Your Onboarding Call</h1>
              <p className="text-neutral-500 dark:text-neutral-400 text-sm mb-8">
                Pick a time that works best for you to meet with our team. We'll walk you through
                the platform and answer any questions you have.
              </p>
              {!meetingBooked && (
                <div className="mt-6 w-full h-[700px] overflow-y-hidden">
                  <iframe
                    src={getMeetingUrl(
                      handle,
                      form.users?.[0]?.name,
                      form.users?.[0]?.email,
                      form.agency.name
                    )}
                    className="w-full h-full"
                  />
                </div>
              )}
              {!!meetingBooked && (
                <div className="mt-6 border-2 border-sky-500 rounded-lg p-4 w-full">
                  <h2 className="text-lg font-medium m-0 p-0">
                    Your onboarding has been scheduled
                  </h2>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-2">
                    An event has been scheduled on your calendar for{' '}
                    {meetingBooked.toLocaleString()}. We'll see you then!
                  </p>
                </div>
              )}
            </Fieldset>

            {/* Agree to Terms and create account */}
            <Fieldset>
              <h1 className="text-2xl font-bold mb-2">Finishing up</h1>
              <p className="text-neutral-500 dark:text-neutral-400 text-sm">
                Your information will be submitted and our team will show you how to sign in and use
                the platform on the scheduled onboarding date. Please don't hesitate to reach out if
                you have any questions. By clicking the button below, you agree to{' '}
                <a
                  href="https://www.withoyster.com/cara-standard-terms"
                  target="_blank"
                  className="text-sky-700 dark:text-sky-300 hover:text-sky-900 dark:hover:text-sky-200 underline"
                >
                  Cara's Terms and Conditions
                </a>
                .
              </p>

              {!meetingBooked && (
                <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-4">
                  Please schedule your onboarding call before creating your account.
                </p>
              )}

              <Button
                type="submit"
                color="sky"
                disabled={!meetingBooked || loading}
                loading={loading}
                className="mt-6"
              >
                Create Account
              </Button>
              {!!error && (
                <div className="mt-4 text-sm/5 text-red-600 sm:text-xs/4 dark:text-red-500">
                  {error}
                </div>
              )}
            </Fieldset>
          </Form>
        </div>
      </div>
    </div>
  );
};
